/**
 * Org-as-code workspace — the org dir for the active layout (`wayai-ws/org/`,
 * or the legacy root-level `org/` for un-migrated repos; see `layout.ts`).
 *
 *   <orgDir>/
 *     skills.yaml            # skill metadata (files stripped, like hub.yaml)
 *     skills/<skill_name>/... # file trees (SKILL.md, docs/…, binaries)
 *
 * Sits beside the hub folders under the same `wayai-ws/` root. One org per repo
 * (org id comes from the workspace manifest `wayai-ws/wayai.yaml`, or the
 * deprecated root `.wayai.yaml`). Reuses the shared skill file-tree
 * primitives so binary/hash/orphan rules match hub-as-code exactly.
 *
 * An older CLI wrote `resources.yaml` and `resources/<slugified name>/`. `org push` and
 * `org diff` refuse that layout; `org pull` converts it (wayai-pro/wayai#5142 step 5 D1b).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { resolveLayout } from './layout.js';
import { readRealFileOrThrow, requireRealSubdirNoSymlink, writeFileNoFollow } from './fs-safety.js';
import { expected } from './expected.js';
import {
  assertDistinctSkillFolders,
  assertOldLayoutConvertible,
  assertSkillEntryType,
  carryEnabled,
  downloadBinaryFiles,
  OLD_RESOURCES_DIR,
  oldResourceFolder,
  removeConvertedOldResources,
  scanResourceFiles,
  skillFolder,
  SKILLS_DIR,
  writeResourceFileTree,
} from './resource-files.js';
import { YAML_DUMP_OPTIONS } from './yaml-writer.js';
import type { OrgSkillsPayload, OrgAsCodeSkill } from './types.js';

export const ORG_MANIFEST_NAME = 'skills.yaml';

/** The manifest an older CLI wrote (`resources:`), which `wayai org pull` converts. */
export const OLD_ORG_MANIFEST_NAME = 'resources.yaml';

export function getOrgDir(gitRoot: string): string {
  return resolveLayout(gitRoot).orgDir;
}

/** Whether the org dir holds a manifest: `skills.yaml`, or an older CLI's `resources.yaml`. */
export function orgManifestExists(orgDir: string): boolean {
  return fs.existsSync(path.join(orgDir, ORG_MANIFEST_NAME)) || fs.existsSync(path.join(orgDir, OLD_ORG_MANIFEST_NAME));
}

/** Whether the org dir is still in the layout an older CLI wrote (`resources.yaml`, `resources/`). */
function orgDirInOldLayout(orgDir: string): boolean {
  return fs.existsSync(path.join(orgDir, OLD_ORG_MANIFEST_NAME)) || fs.existsSync(path.join(orgDir, OLD_RESOURCES_DIR));
}

/**
 * Refuse, before any write, an org pull whose conversion of an older CLI's layout would remove
 * or overwrite what it never compared: anything in `resources/` but the folders of the skills it
 * writes, or anything already in `skills/`. `wayai org pull` runs it before its preview too.
 */
export function assertOrgDirConvertible(orgDir: string, payload: Pick<OrgSkillsPayload, 'skills'>): void {
  assertDistinctSkillFolders(payload.skills ?? []);
  assertOldLayoutConvertible(orgDir, payload.skills ?? [], orgDirInOldLayout(orgDir), 'wayai org pull');
}

export interface ParseOrgSkillsOptions {
  /**
   * Read an org dir still in the pre-skills layout (`resources.yaml`, `resources/`) as its
   * skills, instead of refusing it. Only `wayai org pull` sets it: it compares such a folder
   * before converting it.
   */
  readOldLayout?: boolean;
}

/** Read `org/skills.yaml` + scan `org/skills/<skill_name>/` into a payload. */
export function parseOrgSkills(orgDir: string, opts: ParseOrgSkillsOptions = {}): OrgSkillsPayload {
  const oldParts = [OLD_ORG_MANIFEST_NAME, `${OLD_RESOURCES_DIR}/`].filter((name) => fs.existsSync(path.join(orgDir, name)));
  if (oldParts.length > 0 && !opts.readOldLayout) {
    throw expected(
      `${path.basename(orgDir)}/ is in the old layout (${oldParts.join(', ')}): resources are skills now. `
        + `Run \`wayai org pull\` to convert it (${ORG_MANIFEST_NAME}, each skill's files under ${SKILLS_DIR}/<skill_name>/), `
        + 'then push again. Nothing was sent.',
    );
  }
  const hasManifest = fs.existsSync(path.join(orgDir, ORG_MANIFEST_NAME));
  const hasOldManifest = fs.existsSync(path.join(orgDir, OLD_ORG_MANIFEST_NAME));
  if (hasManifest && hasOldManifest) {
    throw expected(
      `${path.basename(orgDir)}/ holds both ${ORG_MANIFEST_NAME} and ${OLD_ORG_MANIFEST_NAME}: name the skills in ${ORG_MANIFEST_NAME} only.`,
    );
  }
  const old = hasOldManifest;
  const root = path.join(orgDir, old ? OLD_RESOURCES_DIR : SKILLS_DIR);
  // Everything read here is uploaded by `org push`/`org diff`.
  requireRealSubdirNoSymlink(orgDir, root, false);
  const manifestText = readRealFileOrThrow(orgDir, path.join(orgDir, old ? OLD_ORG_MANIFEST_NAME : ORG_MANIFEST_NAME));
  const manifest = ((manifestText !== null ? yaml.load(manifestText) : null) ?? {}) as Record<string, unknown>;
  const section = old ? manifest.resources : manifest.skills;
  const entries = Array.isArray(section) ? (section as Array<Record<string, unknown>>) : [];

  const skills: OrgAsCodeSkill[] = entries.map((entry) => {
    const skill: OrgAsCodeSkill = { name: entry.name as string };
    // An old manifest's `type` (`skill`, or a retired `knowledge` kept since) is the conversion's to drop.
    if (!old) assertSkillEntryType(skill.name, entry.type);
    if (entry.id) skill.id = entry.id as string;
    if (entry.description) skill.description = entry.description as string;
    carryEnabled(entry.enabled, skill);
    if (entry.skill_name) skill.skill_name = entry.skill_name as string;
    if (entry.environment) skill.environment = entry.environment as OrgAsCodeSkill['environment'];
    if (Array.isArray(entry.tags)) skill.tags = entry.tags as string[];
    if (Array.isArray(entry.folders)) skill.folders = entry.folders as OrgAsCodeSkill['folders'];

    const dir = path.join(root, old ? oldResourceFolder(skill) : skillFolder(skill));
    requireRealSubdirNoSymlink(orgDir, dir, false);
    if (fs.existsSync(dir)) {
      const files = scanResourceFiles(dir, '');
      if (files.length > 0) skill.files = files;
    }
    return skill;
  });

  // An old manifest's skills each have their own resources/ folder; the pull writing them checks theirs.
  if (!old) assertDistinctSkillFolders(skills);
  // Always the whole list, `[]` included: removing the last skill deletes it.
  return { version: 1, skills };
}

/**
 * Write `skills.yaml` (files stripped) + file trees + orphan-dir cleanup, converting an org dir an
 * older CLI wrote: its `resources/<slug>` folders of the skills written here are removed, and its
 * `resources.yaml`. Anything else left in `resources/` stops the write before it starts.
 */
export function writeOrgSkills(orgDir: string, payload: OrgSkillsPayload): void {
  const skills = payload.skills ?? [];
  // Before any write: anything in `resources/` but the skills written here (a knowledge backup), or
  // anything already in an old org dir's `skills/`, stops the pull.
  assertOrgDirConvertible(orgDir, payload);
  fs.mkdirSync(orgDir, { recursive: true });
  const skillsDir = path.join(orgDir, SKILLS_DIR);
  // Before any write: the orphan prune below lists skills/ and recursively removes
  // what it finds, so a symlinked skills/ would delete folders at the link target.
  requireRealSubdirNoSymlink(orgDir, skillsDir, false);

  // Manifest carries metadata + folders; file content lives on disk.
  const manifestSkills = skills.map((r) => {
    const { files: _files, ...rest } = r;
    return rest;
  });
  writeFileNoFollow(
    orgDir,
    path.join(orgDir, ORG_MANIFEST_NAME),
    Buffer.from(yaml.dump({ version: 1, skills: manifestSkills }, YAML_DUMP_OPTIONS), 'utf-8'),
  );

  const currentFolders = new Set<string>();
  for (const skill of skills) {
    const folder = skillFolder(skill);
    currentFolders.add(folder);
    writeResourceFileTree(path.join(skillsDir, folder), skill.files || [], orgDir);
  }

  // Remove orphan skill directories.
  if (fs.existsSync(skillsDir)) {
    for (const entry of fs.readdirSync(skillsDir, { withFileTypes: true })) {
      if (entry.isDirectory() && !currentFolders.has(entry.name)) {
        fs.rmSync(path.join(skillsDir, entry.name), { recursive: true, force: true });
      }
    }
  }

  // The older layout, now converted.
  removeConvertedOldResources(orgDir, skills);
  const oldManifest = path.join(orgDir, OLD_ORG_MANIFEST_NAME);
  if (fs.existsSync(oldManifest)) fs.unlinkSync(oldManifest);
}

/** Download binary files (download_url, no inline content) into `org/skills/<skill_name>/`. */
export async function downloadOrgBinaryFiles(orgDir: string, payload: OrgSkillsPayload): Promise<void> {
  let count = 0;
  for (const skill of payload.skills ?? []) {
    if (!skill.files) continue;
    const dir = path.join(orgDir, SKILLS_DIR, skillFolder(skill));
    count += await downloadBinaryFiles(dir, skill.files, orgDir);
  }
  if (count > 0) console.log(`Downloaded ${count} binary skill file(s).`);
}
